import React, { useState, useEffect, useCallback, useRef } from 'react';
import { PowerBIEmbed } from 'powerbi-client-react';
import { models } from 'powerbi-client';
import { getEmbedConfig } from '../services/powerbiService';

const CanvasArea = ({ currentPage, filters, onReportReady }) => {
  const [embedConfig, setEmbedConfig] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const reportRef = useRef(null);

  // Fetch embed token on mount
  useEffect(() => {
    let cancelled = false;

    const fetchEmbed = async () => {
      try {
        setLoading(true);
        setError(null);
        const config = await getEmbedConfig();
        if (!cancelled) {
          setEmbedConfig(config);
          setLoading(false);
        }
      } catch (err) {
        if (!cancelled) {
          console.warn('Power BI embed not available:', err.message);
          setError(err.message);
          setLoading(false);
        }
      }
    };

    fetchEmbed();
    return () => { cancelled = true; };
  }, []);

  // Report loaded callback
  const handleReportLoad = useCallback((report) => {
    reportRef.current = report;
    if (onReportReady) {
      onReportReady(report);
    }
    console.log('✅ Power BI report loaded');
  }, [onReportReady]);

  // Token about to expire — refresh it
  const handleTokenExpiration = useCallback(async () => {
    try {
      const newConfig = await getEmbedConfig();
      setEmbedConfig(newConfig);
      if (reportRef.current) {
        await reportRef.current.setAccessToken(newConfig.token);
        console.log('🔄 Power BI token refreshed');
      }
    } catch (err) {
      console.error('Token refresh failed:', err);
    }
  }, []);

  // If embed config is available, render the Power BI report
  if (embedConfig && !error) {
    return (
      <div className="canvas-area">
        <div className="canvas-frame canvas-frame--embedded">
          <PowerBIEmbed
            embedConfig={{
              type: 'report',
              id: embedConfig.reportId,
              embedUrl: embedConfig.embedUrl,
              accessToken: embedConfig.token,
              tokenType: models.TokenType.Embed,
              settings: {
                panes: {
                  filters: { expanded: false, visible: false },
                  pageNavigation: { visible: false },
                },
                background: models.BackgroundType.Transparent,
                layoutType: models.LayoutType.Custom,
                customLayout: {
                  displayOption: models.DisplayOption.FitToPage,
                },
                navContentPaneEnabled: false,
              },
            }}
            eventHandlers={
              new Map([
                ['loaded', () => console.log('Report loaded event')],
                ['rendered', () => console.log('Report rendered event')],
                ['error', (event) => console.error('PBI Error:', event.detail)],
                ['tokenExpiring', handleTokenExpiration],
              ])
            }
            cssClassName="pbi-embed-container"
            getEmbeddedComponent={(embedded) => handleReportLoad(embedded)}
          />
        </div>
      </div>
    );
  }

  // Fallback: placeholder (no backend running or env not configured)
  return (
    <div className="canvas-area">
      <div className="canvas-frame">
        <div className="canvas-placeholder">
          <div className="canvas-placeholder-icon">📊</div>
          <h3>{currentPage?.label || 'Dashboard'}</h3>

          {loading ? (
            <p className="canvas-loading">Connecting to Power BI...</p>
          ) : error ? (
            <>
              <p>
                Power BI Embedded report will render here once the
                backend proxy is running and the .env is configured.
              </p>
              <div className="canvas-setup-steps">
                <div className="setup-step">
                  <span className="setup-num">1</span>
                  <span>Fill in <code>.env</code> with your Azure AD &amp; Power BI IDs</span>
                </div>
                <div className="setup-step">
                  <span className="setup-num">2</span>
                  <span>Run <code>node server.js</code> to start the embed proxy</span>
                </div>
                <div className="setup-step">
                  <span className="setup-num">3</span>
                  <span>Refresh this page — the report will load automatically</span>
                </div>
              </div>
              <div className="canvas-placeholder-badge">
                <span>⚡</span>
                Power BI Embedded — Awaiting Configuration
              </div>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CanvasArea;
